import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Download, RefreshCw, ShieldAlert } from "lucide-react";
import { SecurityEventRow, EVENT_META } from "@/components/SecurityEventRow";

const fmt = (iso) => new Date(iso).toLocaleString();

export default function SecurityActivity() {
    const { user } = useAuth();
    if (user && user.role !== "admin") return <Navigate to="/" replace />;
    return <SecurityActivityView />;
}

function SecurityActivityView() {
    const [data, setData] = useState({ events: [], flagged_accounts: [] });
    const [filters, setFilters] = useState({ event: "all", person: "", start: "", end: "" });
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                event: filters.event === "all" ? "" : filters.event, person: filters.person,
                start: filters.start ? new Date(filters.start + "T00:00:00").toISOString() : "",
                end: filters.end ? new Date(filters.end + "T23:59:59").toISOString() : "",
            };
            const { data } = await api.get("/security/activity", { params });
            setData(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not load security activity");
        } finally { setLoading(false); }
    }, [filters]);

    useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
    const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

    const exportCsv = () => {
        const head = ["time", "event", "by", "account", "ip", "device", "reason", "burst"];
        const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
        const lines = data.events.map((e) => [fmt(e.created_at), EVENT_META[e.event]?.label || e.event,
            e.actor_email, e.target_email, e.ip || "not recorded", e.device, e.reason, e.burst ? "yes" : ""].map(esc).join(","));
        const blob = new Blob([[head.join(","), ...lines].join("\n")], { type: "text/csv" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `security-activity-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
    };

    return (
        <div data-testid="security-activity-page">
            <PageHeader title="Security Activity"
                        subtitle="Logins, failed logins, password & role changes and suspensions — last 90 days"
                        actions={<div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={load} data-testid="security-refresh-btn">
                                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />Refresh
                            </Button>
                            <Button size="sm" onClick={exportCsv} disabled={!data.events.length} data-testid="security-export-btn">
                                <Download className="w-4 h-4 mr-1" />Export CSV
                            </Button>
                        </div>} />
            <div className="px-8 py-6 space-y-4">
                {data.flagged_accounts.length > 0 && <FlaggedBanner accounts={data.flagged_accounts} onPick={(e) => set("person", e)} />}
                <Filters filters={filters} set={set} />
                <Card>
                    <CardContent className="p-0 overflow-x-auto">
                        <table className="w-full text-sm" data-testid="security-events-table">
                            <thead className="bg-secondary/40 text-xs text-muted-foreground">
                                <tr>{["Time", "Event", "By", "Account", "IP address", "Device"].map((h) => (
                                    <th key={h} className="text-left font-medium px-4 py-2.5 whitespace-nowrap">{h}</th>
                                ))}</tr>
                            </thead>
                            <tbody>
                                {data.events.map((e) => <SecurityEventRow key={e.id} e={e} />)}
                                {!data.events.length && (
                                    <tr><td colSpan={6} className="text-center text-muted-foreground py-10" data-testid="security-empty">
                                        {loading ? "Loading..." : "No security events match these filters"}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>
                <div className="text-xs text-muted-foreground" data-testid="security-count">{data.events.length} events</div>
            </div>
        </div>
    );
}

function Filters({ filters, set }) {
    return (
        <div className="flex flex-wrap items-end gap-3" data-testid="security-filters">
            <Select value={filters.event} onValueChange={(v) => set("event", v)}>
                <SelectTrigger className="w-52" data-testid="security-filter-event"><SelectValue /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">All events</SelectItem>
                    {Object.entries(EVENT_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                </SelectContent>
            </Select>
            <Input placeholder="Person (email)" value={filters.person} className="w-60"
                   onChange={(e) => set("person", e.target.value)} data-testid="security-filter-person" />
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                From <Input type="date" className="w-40" value={filters.start}
                            onChange={(e) => set("start", e.target.value)} data-testid="security-filter-start" />
                to <Input type="date" className="w-40" value={filters.end}
                          onChange={(e) => set("end", e.target.value)} data-testid="security-filter-end" />
            </div>
        </div>
    );
}

function FlaggedBanner({ accounts, onPick }) {
    return (
        <div className="flex items-start gap-3 p-4 rounded-md border border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-900"
             data-testid="security-flagged-banner">
            <ShieldAlert className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="text-sm">
                <div className="font-semibold text-red-800 dark:text-red-300">Repeated failed logins detected</div>
                <div className="text-xs text-red-700 dark:text-red-400 mt-1 flex flex-wrap gap-2">
                    5+ failures within an hour for:
                    {accounts.map((a) => (
                        <button key={a.email} onClick={() => onPick(a.email)} className="underline font-mono"
                                data-testid={`security-flagged-${a.email}`}
                                title={`${a.total} failed logins in the last 90 days`}>
                            {a.email} ({a.failures} in 1h)
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
